import { Component, DestroyRef, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { ChangelogService } from 'src/app/services/gui/changelog/changelog.service';
import { API, AuthenticationOptionDTO, AuthenticationType, UserClientFullDTO } from '../../services/api/api';
import { ErrorService } from '../../services/error.service';
import { UserService } from '../../services/user.service';
import { NavigationService } from '@ngx-seatback/utils';
import { RoutingID } from '../../routes/sb-routing-enum';
import { UtilService } from '../../services/util.service';
import { ToasterService, ToastType } from '../../services/toaster/toaster.service';
import { ErrorCode } from '../../services/api/errorCodes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { QueryParamService } from '../../services/lists/query-params';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  protected readonly API = API;

  submitted: boolean;
  redirectLink: string;
  loading: boolean;

  form = new UntypedFormGroup({
    username: new UntypedFormControl('', [Validators.required]),
    password: new UntypedFormControl('', [Validators.required]),
  });

  redirectSet: boolean = false;
  authOptions: AuthenticationOptionDTO[] | null;
  classicLoginOption: AuthenticationOptionDTO;
  hasIDPOption: boolean;
  // adminKeyLoginOption: AuthenticationOptionDTO;
  isAuthenticationFailed: boolean;
  hasLoggedOut: boolean;

  constructor(
    public readonly api: API,
    private readonly router: Router,
    private readonly changelogService: ChangelogService,
    private readonly errorHandler: ErrorService,
    public readonly userService: UserService,
    private readonly navService: NavigationService,
    public readonly util: UtilService,
    public readonly toastHandler: ToasterService,
    private readonly queryParamService: QueryParamService,
    private readonly destroyRef: DestroyRef
  ) {
    this.hasLoggedOut = false;
    this.authOptions = null;
    this.loading = true;
    this.classicLoginOption = null;
    this.isAuthenticationFailed = false;
    this.hasIDPOption = false;
    this.submitted = false;
  }

  get username() {
    return this.form.get('username');
  }

  get password() {
    return this.form.get('password');
  }

  ngOnInit() {
    const qpTime = Number(this.queryParamService.getQueryParam('time'));
    // valid time 15 min
    const validLoginTime = isNaN(qpTime) ? false : qpTime + 15 * 60 * 1000 > DateTime.now().toMillis();
    const qpType = this.queryParamService.getQueryParam('type');
    const qpCode = this.queryParamService.getQueryParam('code');
    const qpRedirect = this.queryParamService.getQueryParam('redirect');
    const qpLoggedOut = this.queryParamService.getQueryParam('loggedOut');

    if (!validLoginTime) {
      this.queryParamService.clearQueryParam('type');
      this.queryParamService.clearQueryParam('code');
      this.queryParamService.clearQueryParam('time');
      this.queryParamService.clearQueryParam('session_state');
    }

    if (validLoginTime && qpType && qpCode && qpTime) {
      this.api
        .de_bpr_systems_web_api_rest_Authentication_executeIDPLoginWithCode(qpType, {
          code: qpCode,
          redirectURL: this.getEncodedRedirectURL(qpType, qpTime, qpRedirect),
        })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.retrieveLoginOptions(false);
          })
        )
        .subscribe({
          next: data => {
            if (data instanceof UserClientFullDTO) {
              this.setUserClient(data);
            } else {
              this.isAuthenticationFailed = true;
            }
          },
          error: err => {
            this.errorHandler.errorHandler(err);
            this.isAuthenticationFailed = true;
          },
        });
    } else {
      this.retrieveLoginOptions();
      this.retrieveStatus();
    }
    if (qpLoggedOut) {
      this.queryParamService.clearQueryParam('loggedOut');
      this.hasLoggedOut = true;
    }
    if (this.redirectSet) {
      this.router
        .navigate([this.router.url.split('?')[0]], { queryParams: { redirect: this.redirectLink, ...this.queryParamService.queryParams } })
        .then();
    } else {
      this.setRedirectLink(this.queryParamService.queryParams);
    }
  }

  private retrieveLoginOptions(redirect = true) {
    this.api
      .de_bpr_systems_web_api_rest_Authentication_retrieveAllowedOptions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (authOptions: AuthenticationOptionDTO[]) => {
          this.authOptions = authOptions;
          this.classicLoginOption = authOptions.find(option => option.type === AuthenticationType.CREDENTIALS);
          this.hasIDPOption = authOptions.some(option => option.type === AuthenticationType.IDP);
          if (this.authOptions?.length === 1 && this.authOptions[0].type === AuthenticationType.IDP && redirect && !this.hasLoggedOut) {
            this.selectedOption(this.authOptions[0]);
          }
        },
        error: error => {
          this.authOptions = [];
          this.errorHandler.errorHandler(error);
        },
      });
  }

  setRedirectLink(params: Params) {
    if (!this.redirectSet) {
      this.redirectSet = true;
      this.redirectLink = params.redirect;
      this.router.navigate([this.router.url.split('?')[0]], { queryParams: { redirect: params.redirect, ...params } }).then();
    }
  }

  retrieveStatus() {
    this.api
      .shared_Status_status()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (userData: UserClientFullDTO) => {
          this.setUserClient(userData);
        },
        error: error => {
          if (error?.status < 400 || error?.status >= 500) {
            this.errorHandler.errorHandler(error);
          }
        },
      });
  }

  getFrontendVersion(): string {
    return this.changelogService.getFrontendVersion();
  }

  getBackendVersion(): string {
    return this.changelogService.getBackendVersion();
  }

  getFrontendName() {
    return this.changelogService.getFrontendName();
  }

  getBackendName() {
    return this.changelogService.getBackendName();
  }

  onSubmit(option: AuthenticationOptionDTO): void {
    this.submitted = true;

    if (!option) {
      throw new Error('No Auth Option provided');
    }

    if (!this.form.valid) {
      return;
    }

    this.resetMessage();
    this.userService.user = null;
    this.userService.userGeoLocation = null;
    this.api
      // .auth_Authentication_login(this.form.value)
      .de_bpr_systems_web_api_rest_Authentication_executeNativeLogin(option.id, this.form.value)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (userClient: UserClientFullDTO) => {
          this.setUserClient(userClient);
        },
        error: error => {
          if (error?.status < 400 || error?.status >= 500) {
            this.errorHandler.errorHandler(error);
          } else {
            if (error.status === 400 && error.error.errorCode && ErrorCode.PASSWORD_REQUIREMENTS_NOT_MET === error.error.errorCode) {
              this.toastHandler.toast(ToastType.DANGER, error.error.message);
              this.navService.navigateToPoint(RoutingID.passwordReset, null, { queryParams: { email: this.username.value } });
            }
            if (error.status === 401) {
              this.form.setErrors({ serverError: true });
            }
          }
        },
      });
  }

  selectedOption($event: AuthenticationOptionDTO) {
    if (!$event) {
      return;
    }
    this.resetMessage();
    this.loading = true;
    this.api
      .de_bpr_systems_web_api_rest_Authentication_retrieveIDPLoginUrl($event.id, {
        no_redirect: true,
        redirectURL: this.getEncodedRedirectURL($event.id, DateTime.now().toMillis(), this.redirectLink),
      })
      .subscribe(url => {
        window.open(url, '_self');
      });
  }

  getEncodedRedirectURL(authOptionId: string, time: number, redirectLink?: string) {
    let encodedRedirectURL = 'login?type=' + authOptionId;
    //add timestamp for login
    if (time) {
      encodedRedirectURL += '&time=' + time;
    }
    if (redirectLink && redirectLink !== 'undefined') {
      encodedRedirectURL += '&redirect=' + encodeURIComponent(redirectLink);
    }
    return encodeURIComponent(encodedRedirectURL);
  }

  private resetMessage() {
    this.isAuthenticationFailed = false;
    this.hasLoggedOut = false;
    this.form.setErrors(null);
  }

  private setUserClient(userClient: UserClientFullDTO) {
    this.userService.user = userClient;
    if (this.redirectLink && this.redirectLink !== '/') {
      this.router.navigateByUrl(this.redirectLink).then();
    } else {
      this.navService.navigateToPoint(RoutingID.home, { id: 'clientID', value: userClient.client.id });
    }
  }
}
